











import { computed, defineComponent, onBeforeUnmount, ref } from "@vue/composition-api";
import { ContentPriceSubject, ContentPriceSubjectTypeEnum } from "@scrile/api-provider/dist/api/ContentPriceProvider";
import { PaymentPackageEnum } from "@scrile/api-provider/dist/api/PaymentPackageProvider";
import { PurchaseSubjectTypeEnum } from "@scrile/api-provider/dist/api/PurchaseTypeProvider";
import { ContentFeed } from "@scrile/api-provider/dist/projects/scrileConnect/ContentFeedProvider";
import useOneTimePurchase from "@/hooks/useOneTimePurchase";
import usePaymentPackages from "@/hooks/usePaymentPackages";
import emitter from "@/lib/emitter";
import { $t } from "@/lib/i18nHelpers";
import { getFullPriceData } from "@/lib/moneyHelpers";
import { PurchaseDetails } from "@/payments/types";
import { ContentFeedPurchaseEvent, NotificationData } from "@/types";
import { EVENTS } from "@/const";
import { PaymentConfirmationData } from "@/components/PaymentConfirmation/const";
import UnlockFeedView from "./components/UnlockFeedView.vue";

export default defineComponent({
  name: "UnlockFeed",
  components: { UnlockFeedView },
  setup() {
    const { paymentPackages, getPaymentPackages } = usePaymentPackages([PaymentPackageEnum.FILESET]);
    const loading = ref(false);
    const purchaseProcessing = ref(false);
    const selectedFeed = ref<ContentFeed | null>(null);
    const typeOfPurchase = ref<Omit<ContentFeedPurchaseEvent, "feed"> | undefined>();
    const paymentSystemId = ref("");

    const priceData = computed(() =>
      getFullPriceData(paymentPackages.value.find(p => p.id === selectedFeed.value?.pricePackageId))
    );
    const data = computed<ContentPriceSubject>(() => ({
      subjectId: selectedFeed.value?.id ?? "0",
      subjectType: ContentPriceSubjectTypeEnum.CONTENT_SET,
    }));
    const purchaseDetails = computed<PurchaseDetails>(() => ({
      pricePackageId: selectedFeed.value?.pricePackageId ?? "0",
      price: priceData.value.price,
      paymentSystemId: paymentSystemId.value,
      subject: {
        subjectId: selectedFeed.value?.id ?? "0",
        subjectType: PurchaseSubjectTypeEnum.CONTENT_SET,
      },
    }));
    const { performOneTimePurchase } = useOneTimePurchase(data, purchaseDetails);

    const close = () => {
      selectedFeed.value = null;
    };

    const handleOneTimePurchase = async (data: PaymentConfirmationData) => {
      if (!selectedFeed.value) return;
      try {
        purchaseProcessing.value = true;
        paymentSystemId.value = data.paymentSystemId ?? "";
        const response = await performOneTimePurchase(data.fromBalance);
        if (response.success) {
          emitter.emit(EVENTS.CONTENT_FEED_PURCHASED, {
            userId: selectedFeed.value.userId,
            feedId: selectedFeed.value.id,
          });
          emitter.emit<NotificationData>(EVENTS.NOTIFICATION_SHOW, {
            text: $t("One time buy {screenName}'s content for {money}", {
              screenName: selectedFeed.value.user.screenName,
              money: data.fromBalance ? priceData.value.creditPrice : priceData.value.price,
            }),
            image: { user: selectedFeed.value.user },
            button: { text: $t("Close") },
            position: "top",
          });
          close();
        }
      } finally {
        purchaseProcessing.value = false;
      }
    };

    const showUnlockFeed = async (eventData?: ContentFeedPurchaseEvent) => {
      if (loading.value || !eventData) return;
      const { feed, ...others } = eventData;
      typeOfPurchase.value = others;
      try {
        loading.value = true;
        await getPaymentPackages();
        selectedFeed.value = feed;
      } finally {
        loading.value = false;
      }
    };

    emitter.on<ContentFeedPurchaseEvent>(EVENTS.CONTENT_FEED_PURCHASE, showUnlockFeed);
    onBeforeUnmount(() => {
      emitter.off<ContentFeedPurchaseEvent>(EVENTS.CONTENT_FEED_PURCHASE, showUnlockFeed);
    });

    return {
      selectedFeed,
      paymentPackages,
      loading,
      purchaseProcessing,
      typeOfPurchase,
      priceData,
      handleOneTimePurchase,
      close,
    };
  },
});
